<template>
  <div
    :class="[
      'ui-notification',
      {
        'ui-notification-shake': shaking,
        [`ui-notification_${type}`]: !color && type,
      },
      `ui-notification__${size}`,
    ]"
    :style="{ borderColor: color }"
  >
    <div class="ui-notification-content">
      <div v-if="title" class="ui-notification-title" v-text="title" />
      <div v-if="$slots.default" class="ui-notification-subtitle">
        <slot />
      </div>
    </div>
    <div v-if="$slots.action" class="ui-notification-action">
      <slot name="action" />
    </div>
    <div
      v-if="closable"
      ref="closeButton"
      class="ui-notification-close"
      @click="onCloseHandler"
    >
      <f-sprite-icon :icon-link="$options.icons.IconClose" />
    </div>
  </div>
</template>

<script>
import isFunction from 'lodash.isfunction';

import IconClose from '../../../assets/icons/close.svg';

export const TYPES = {
  error: 'error',
  success: 'success',
  warning: 'warning',
};

const SIZES = {
  small: 'small',
  normal: 'normal',
};

export default {
  name: 'UiNotification',

  icons: {
    IconClose,
  },

  props: {
    title: {
      type: String,
      default: '',
    },

    color: {
      type: String,
      default: undefined,
    },

    type: {
      type: String,
      default: undefined,
      validator(value) {
        return typeof value === 'undefined' || Object.values(TYPES).includes(value);
      },
    },

    size: {
      type: String,
      default: 'normal',
      validator: (v) => Object.values(SIZES).includes(v),
    },

    closable: {
      type: Boolean,
      default: false,
    },

    onShown: {
      type: [Function, undefined],
      default: undefined,
    },

    onClose: {
      type: [Function, undefined],
      default: undefined,
    },
  },

  data: () => ({
    shaking: false,
  }),

  mounted() {
    if (isFunction(this.onShown)) this.onShown();
  },

  methods: {
    shake() {
      this.shaking = true;
      setTimeout(() => {
        this.shaking = false;
      }, 600);
    },
    onCloseHandler() {
      if (isFunction(this.onClose)) this.onClose();
      this.$emit('remove');
    },
  },
};
</script>

<style lang="scss">
.ui-notification {
  $self: &;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border-radius: 4px;
  border-left: 4px solid $blue_l;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .04), 0 8px 24px rgba(0, 0, 0, .03), 0 16px 32px rgba(0, 0, 0, .02);
  background: $white;
  padding: 16px;
  margin: 24px 0;
  transition: border-color ease .2s;

  &__small {
    padding: 12px;

    #{$self}-subtitle {
      margin-top: 0;
      font-size: 12px;
      line-height: 16px;
    }
  }

  &_error {
    border-color: $red;
    background: linear-gradient(0deg, #f3474410, #f3474410), #fff;
  }

  &_error &-title {
    color: $red;
  }

  &_success {
    border-color: $blue_l;
    background: linear-gradient(0deg, #95d4e920, #95d4e920), #fff;
  }

  &_warning {
    border-color: $orange_f;
    background: linear-gradient(0deg, #ffb71b10, #ffb71b10), #fff;
  }

  &-content {
    width: 100%;
  }

  &-action {
    margin: 12px 0 0;
    display: flex;
    > .ui-button {
      &:not(:first-child) {
        margin-left: 12px;
      }
    }
  }

  &-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }

  &-subtitle {
    font-size: 14px;
    line-height: 20px;
    margin-top: 4px;
    color: $gray;
  }

  &-close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    color: $gray_lu;
  }

  &.ui-notification-shake {
    animation: notification-shake .6s ease-out both;
  }
}

@include breakpoint("tablet") {
  .ui-notification {
    &-action {
      margin: 0 0 0 auto;
    }
  }
}

@include breakpoint("desktop") {
  .ui-notification {
    flex-direction: row;

    &-content {
      width: auto;
    }
  }
}

@keyframes notification-shake {
  10% {
    transform: translate3d(-2px, 0, 0);
  }
  20%,
  90% {
    transform: translate3d(6px, 0, 0);
  }
  50%,
  70% {
    transform: translate3d(-8px, 0, 0);
  }
  40%,
  60% {
    transform: translate3d(8px, 0, 0);
  }
}
</style>
